import { ProductItem, EventItem, SubscriptionProduct, SubscriptionEmailAdminListItemDto, SubscriptionEmailAdminDetailDto, SubscriptionMonthlyThemesDto } from './types';

export const apiBase = process.env.REACT_APP_API_BASE_URL || "";

async function request<T>(path: string, options: RequestInit = {}, token?: string | null): Promise<T> {
  const res = await fetch(`${apiBase}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    },
    ...options
  });
  if (!res.ok) throw new Error(`Request failed ${res.status}`);
  return res.json();
}


export const api = {
  login: (email: string, password: string) => request<{ token: string; id: number; email: string; name: string }>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  register: (email: string, name: string, password: string) => request<{ id: number; email: string; name: string }>("/auth/register", { method: "POST", body: JSON.stringify({ email, name, password }) }),

  adminLogin: (email: string, password: string) => request<{ token: string; email: string; name: string }>(
    "/admin/login",
    { method: "POST", body: JSON.stringify({ email, password }) }
  ),

  adminListMonthlyEmails: (token: string) => request<SubscriptionEmailAdminListItemDto[]>(
    "/admin/subscription-emails/monthly",
    {},
    token
  ),

  adminGetSubscriptionEmail: (token: string, id: number) => request<SubscriptionEmailAdminDetailDto>(
    `/admin/subscription-emails/${id}`,
    {},
    token
  ),

  adminUpsertMonthlyEmail: (
    token: string,
    payload: {
      effectiveDate: string; // YYYY-MM-DD
      subject: string;
      colorHex: string; // #RRGGBB
      bodyText: string;
      isActive: boolean;
      attachmentFileName?: string | null;
      attachmentContentType?: string | null;
      attachmentBase64?: string | null;
    }
  ) => request<SubscriptionEmailAdminListItemDto>(
    "/admin/subscription-emails/monthly",
    { method: "POST", body: JSON.stringify(payload) },
    token
  ),

  adminSetSubscriptionEmailActive: (token: string, id: number, isActive: boolean) => request<void>(
    `/admin/subscription-emails/${id}/active`,
    { method: "POST", body: JSON.stringify({ isActive }) },
    token
  ),

  adminListWelcomeGifts: (token: string) => request<SubscriptionEmailAdminListItemDto[]>(
    "/admin/subscription-emails/welcome-gift",
    {},
    token
  ),

  adminCreateWelcomeGift: (
    token: string,
    payload: {
      subject: string;
      bodyText: string;
      isActive: boolean;
      attachmentFileName?: string | null;
      attachmentContentType?: string | null;
      attachmentBase64?: string | null;
    }
  ) => request<SubscriptionEmailAdminListItemDto>(
    "/admin/subscription-emails/welcome-gift",
    { method: "POST", body: JSON.stringify(payload) },
    token
  ),

  adminUpdateWelcomeGift: (
    token: string,
    id: number,
    payload: {
      subject: string;
      bodyText: string;
      isActive: boolean;
      attachmentFileName?: string | null;
      attachmentContentType?: string | null;
      attachmentBase64?: string | null;
    }
  ) => request<SubscriptionEmailAdminListItemDto>(
    `/admin/subscription-emails/welcome-gift/${id}`,
    { method: "POST", body: JSON.stringify(payload) },
    token
  ),

  events: async () => {
    const data = await request<EventItem[]>("/events");
    return (Array.isArray(data) ? data : []).map((e) => ({
      ...e,
      eventType: e.eventType || 'workshop',
    }));
  },
  registerEvent: (eventId: number, payload: { userAccountId?: number; guestEmail?: string; guestName?: string }, token?: string | null) => request(`/events/${eventId}/register`, { method: "POST", body: JSON.stringify(payload) }, token),
  products: () => request<ProductItem[]>("/products"),
  purchaseProduct: (payload: { productId: number; userAccountId?: number; guestEmail?: string; guestName?: string }, token?: string | null) => request("/purchases", { method: "POST", body: JSON.stringify(payload) }, token),
  plans: () => request<SubscriptionProduct[]>("/subscriptions/plans"),
  subscriptionThemes: () => request<SubscriptionMonthlyThemesDto>("/subscriptions/themes"),
  subscribe: (payload: { userAccountId: number; subscriptionPlanId: number }, token?: string | null) => request("/subscriptions", { method: "POST", body: JSON.stringify(payload) }, token),
  cancelSubscription: (id: number, token?: string | null) => request(`/subscriptions/${id}/cancel`, { method: "POST" }, token),
  accountOverview: (userId: number, token?: string | null) => request(`/account/overview/${userId}`, {}, token)
};
